import * as React from 'react';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import type {LinkConfig} from './link-item';
import {SidebarNavItems} from './sidebar-nav-items';

export type NavItem = {
    id: string;
    label?: string;
    links: LinkConfig[];
    shouldHide?: boolean;
    footer?: React.ReactNode;
};

type OwnProps = DefaultProps & {
    navItems: NavItem[];
    navItemsBeforeHeaderComponent?: NavItem[];
    includeSearch?: boolean;
    footer?: React.ReactNode;
    headerComponent?: React.ReactNode;
    isFullHeight?: boolean;
};

type DefaultProps = {
    searchPlaceholder: string;
};

type Props = OwnProps & ContextRouter;

type State = {
    searchString: string;
};

export class SidebarNavComponent extends React.Component<Props, State> {
    unlisten: (...args: any) => any;

    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            searchString: '',
        };
        this.unlisten = () => {};
    }

    static defaultProps = {
        searchPlaceholder: 'Search…',
    };

    componentDidMount() {
        // Clear the search when navigating
        this.unlisten = this.props.history.listen(() => {
            this.setState({
                searchString: '',
            });
        });
    }

    componentWillUnmount() {
        this.unlisten();
    }

    render() {
        const allNavItems = this.props.navItemsBeforeHeaderComponent
            ? [...this.props.navItemsBeforeHeaderComponent, ...this.props.navItems]
            : this.props.navItems;

        // Get a non-unique list of ids that we'll want to keep in the sidebar.
        // It's okay to have duplicate ids here.
        const filteredDisplayLinkIds = this.getNavItemsToDisplay(allNavItems);

        return (
            <SidebarNavItems
                navItems={this.props.navItems}
                navItemsBeforeHeaderComponent={this.props.navItemsBeforeHeaderComponent}
                handleSearchChange={this.handleSearchChange}
                includeSearch={this.props.includeSearch}
                searchPlaceholder={this.props.searchPlaceholder}
                footer={this.props.footer}
                headerComponent={this.props.headerComponent}
                filteredDisplayLinkIds={filteredDisplayLinkIds}
                isFullHeight={this.props.isFullHeight}
            />
        );
    }

    handleSearchChange = (search: string) => {
        this.setState({
            searchString: search,
        });
    };

    getNavItemsToDisplay(navItems: NavItem[]) {
        return navItems.reduce<Array<any>>((ids, section) => {
            const displayedLinks = section.links.filter((link) => !link.isHidden);

            return [
                ...ids,
                ...displayedLinks
                    .filter(linkTextFilter(this.state.searchString))
                    .map((link) => link.id),
                ...displayedLinks
                    .filter(linkTagFilter(this.state.searchString))
                    .map((link) => link.id),
                ...displayedLinks
                    .filter(sectionLabelFilter(this.state.searchString, section))
                    .map((link) => link.id),
            ];
        }, []);
    }
}

// @ts-expect-error
export const SidebarNav = withRouter<OwnProps & DefaultProps>(SidebarNavComponent);

/**
 * Returns a function that can be used as a filter function to keep links
 * which have a label (`linkText`) which includes the current search string.
 *
 * @param  {string} search The current value of the search
 * @return {Function}      Filter function for links
 */
function linkTextFilter(search: string) {
    return function (link: LinkConfig) {
        if (search === '') return true;

        return link.linkText.toLowerCase().includes(search.toLowerCase());
    };
}

/**
 * Returns a function that can be used to keep links which contain a tag matching
 * the entire current string.
 *
 * @param  {string} search The current value of the search
 * @return {Function}      Filter function for links
 */
function linkTagFilter(search: string) {
    return function (link: LinkConfig) {
        if (search === '') return true;
        if (!link.tags || !Array.isArray(link.tags) || !link.tags.length) return false;

        return link.tags.map((tag) => tag.toLowerCase()).includes(search.toLowerCase());
    };
}

/**
 * Returns a function that can be used as a filter function to keep links in
 * a section with a label containing the search string.
 *
 * @param  {string} search  The current value of the search
 * @param  {Object} section The section the links are in
 * @return {Function}       Filter function for links
 */
function sectionLabelFilter(search: string, section: NavItem) {
    return function () {
        if (search === '') return true;

        // If we don't have a section label, don't search the section label
        return section.label
            ? section.label.toLowerCase().includes(search.toLowerCase())
            : () => false;
    };
}
